import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axiosInstance";
import CustomModal from "../../../components/modal/CustomModal";
import UserCreateForm from "../../user/components/UserCreateForm";
import CompanyForm from "../../company/component/CompanyForm";
import VehicleFrom from "../../user/components/VehicleFrom";
import DeleteUser from "./DeleteUser";
import DeleteApartments from "./DeleteApartments";
import AppartmentForm from "../../appartment/components/AppartmentForm";
import DeleteVehicle from "./DeleteVehicle";
import ZoneForm from "./ZoneForm";
import DeleteZone from "./DeleteZone";

const RequestList = () => {
  const [requests, setRequests] = useState<any>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [formData, setFromData] = useState<any>();
  const [id, setId] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  // Check if current user can approve requests (Super Admin or "admin" permission)
  // Super Admin = role = 0, Admin permission users = have "admin" permission but are NOT Super Admin
  const role = Number(localStorage.getItem("role"));
  const isSuperAdmin = role === 0 || localStorage.getItem("isSuperAdmin") === "true";
  const permissionsStr = localStorage.getItem("permissions");
  let permissions: string[] = [];
  try {
    permissions = permissionsStr ? JSON.parse(permissionsStr) : [];
  } catch (e) {
    console.error("Error parsing permissions:", e);
    permissions = [];
  }
  const hasAdminPermission = permissions.includes("admin");
  const canApproveRequests = isSuperAdmin || hasAdminPermission;

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setData(null);
    setFromData(null);
    setId(null);
    getRequest();
  };

  const handleSelectAction = async (id: any) => {
    setId(id);
    // Load request data first, then open modal
    try {
      const resp: any = await axiosInstance.get(`/api/request/${id}`);
      if (resp.status === 200) {
        const raw = JSON.parse(resp?.data?.data?.data || "[]");
        // Backend stores params as JSON array (e.g. [payload]); use first element so form gets the object
        const parseForm = Array.isArray(raw) && raw.length > 0 ? raw[0] : raw;
        setFromData(parseForm);
        setData(resp.data?.data);
        // Open modal after data is loaded
        handleOpen();
      }
    } catch (error: any) {
      toast.error("Error fetching request details", { autoClose: 3000 });
    }
  };
  
  const getRequest = async () => {
    setLoading(true);
    try {
      const resp: any = await axiosInstance.get("/api/request");
      if (resp.status === 200) {
        setRequests(resp?.data?.data);
      }
    } catch (error: any) {
      toast.error("Error fetching requests", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handelRejectRequest = async (id: any) => {
    try {
      const resp: any = await axiosInstance.put(`/api/request/${id}`, {
        status: "2",
      });
      if (resp.status === 200) {
        toast.success("Request rejected successfully");
        getRequest();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.body);
    }
  };

  useEffect(() => {
    getRequest();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Request Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Review and manage system requests
              </p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
              <p className="text-gray-600 dark:text-gray-300">Loading requests...</p>
            </div>
          </div>
        ) : requests?.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <i className="fa-solid fa-inbox text-3xl text-gray-400 dark:text-gray-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                No requests found
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                There are no pending requests at the moment
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      SI No.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Process
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Request From
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {requests?.map((item: any, index: any) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold">
                            {(() => {
                              const actionMap: Record<string, string> = {
                                "user-create": "Create Member",
                                "user-update": "Update Member",
                                "user-delete": "Delete Member",
                                "apartment-create": "Create Apartment",
                                "apartment-update": "Update Apartment",
                                "apartment-delete": "Delete Apartment",
                                "vehicle-create": "Create Vehicle",
                                "vehicle-update": "Update Vehicle",
                                "vehicle-delete": "Delete Vehicle",
                                "zone-create": "Create Zone",
                                "zone-update": "Update Zone",
                                "zone-delete": "Delete Zone",
                                "company-create": "Create Company",
                                "company-update": "Update Company",
                              };
                              return actionMap[item?.action] || item?.action;
                            })()}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {(() => {
                              try {
                                const parsed = JSON.parse(item?.data);
                                const data = Array.isArray(parsed) ? parsed[0] : parsed;
                                if (item?.action?.includes("apartment")) {
                                  return data?.appartment_name || data?.data?.appartment_name || "N/A";
                                }
                                if (item?.action?.includes("user")) {
                                  return data?.name || data?.data?.name || "N/A";
                                }
                                if (item?.action?.includes("vehicle")) {
                                  return data?.reg_no || data?.data?.reg_no || "N/A";
                                }
                                if (item?.action?.includes("zone")) {
                                  return data?.name || data?.zone_name || "N/A";
                                }
                                if (item?.action?.includes("company")) {
                                  return data?.name || "N/A";
                                }
                                return "N/A";
                              } catch {
                                return "N/A";
                              }
                            })()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {item?.requester?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item?.is_approved == 0 && (
                          <span className="px-2 py-0.5 rounded text-xs font-semibold text-yellow-600 bg-yellow-50 dark:bg-yellow-900/30 dark:text-yellow-300">
                            Pending
                          </span>
                        )}
                        {item?.is_approved == 1 && (
                          <span className="px-2 py-0.5 rounded text-xs font-semibold text-green-600 bg-green-50 dark:bg-green-900/30 dark:text-green-300">
                            Approved
                          </span>
                        )}
                        {item?.is_approved == 2 && (
                          <span className="px-2 py-0.5 rounded text-xs font-semibold text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-300">
                            Rejected
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {item?.is_approved == 0 && canApproveRequests && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSelectAction(item?.id)}
                              className="btn-primary px-3 py-1 text-xs"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handelRejectRequest(item?.id)}
                              className="btn-primary px-3 py-1 text-xs"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                        {item?.is_approved == 0 && !canApproveRequests && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Pending approval
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <CustomModal open={open} onClose={handleClose}>
        {!data && (
          <div className="p-6">
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
            </div>
            <p className="text-center text-gray-600 dark:text-gray-300">Loading request details...</p>
          </div>
        )}
        {data && (data?.action == "user-create" || data?.action == "user-update") && (
          <UserCreateForm
            onClose={handleClose}
            id={data?.action == "user-update" ? formData?.id : undefined}
            requestData={formData}
            requestId={id}
          />
        )}
        {data && (data?.action === "company-create" ||
          data?.action === "company-update") && (
          <CompanyForm
            onClose={handleClose}
            requestData={Array.isArray(formData) ? formData?.[0] : formData}
            requestId={id}
          />
        )}

        {data && (data?.action === "apartment-create" ||
          data?.action === "apartment-update") && (
          <AppartmentForm
            onClose={handleClose}
            requestData={Array.isArray(formData) ? formData?.[0] : formData}
            requestId={id}
          />
        )}

        {data && (data?.action === "vehicle-create" ||
          data?.action === "vehicle-update") && (
          <VehicleFrom
            onClose={handleClose}
            requestData={Array.isArray(formData) ? formData?.[0] : formData}
            requestId={id}
          />
        )}

        {data && data?.action === "user-delete" && (
          <DeleteUser
            onClose={handleClose}
            requestData={Array.isArray(formData) ? formData?.[0] : formData}
            requestId={id}
          />
        )}

        {data && data?.action === "apartment-delete" && (
          <DeleteApartments
            onClose={handleClose}
            requestData={Array.isArray(formData) ? formData?.[0] : formData}
            requestId={id}
          />
        )}
        {data && data?.action === "vehicle-delete" && (
          <DeleteVehicle
            onClose={handleClose}
            requestData={Array.isArray(formData) ? formData?.[0] : formData}
            requestId={id}
          />
        )}

        {data && (data?.action === "zone-create" ||
          data?.action === "zone-update") && (
          <ZoneForm
            onClose={handleClose}
            requestData={Array.isArray(formData) ? formData?.[0] : formData}
            requestId={id}
          />
        )}

        {data && data?.action === "zone-delete" && (
          <DeleteZone
            onClose={handleClose}
            requestData={Array.isArray(formData) ? formData?.[0] : formData}
            requestId={id}
          />
        )}

        {data && !["user-create", "user-update", "user-delete", "company-create", "company-update", "apartment-create", "apartment-update", "apartment-delete", "vehicle-create", "vehicle-update", "vehicle-delete", "zone-create", "zone-update", "zone-delete"].includes(data?.action) && (
          <div className="p-6">
            <p className="text-center text-gray-600 dark:text-gray-300">
              Unknown request type: {data?.action}
            </p>
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default RequestList;
